import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../lib/icons';
import { GlobalSearch } from '../components/GlobalSearch';
import { PreferenceToggles, usePreferences } from '../lib/PreferencesProvider';
import { useAuth } from '../auth/AuthProvider';
import { useSidebar } from './SidebarToggle';
import { NotificationBell } from './Notifications';

/** Initials for the avatar, matching the prototype's two-letter chip. */
function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

/**
 * Topbar, ported from the prototype's `renderTopbar`.
 *
 * The notification panel is live (FEAT-014), as is global search (FEAT-015) and
 * the signed-in identity. The prototype's role switcher has no production
 * equivalent at all: it stood in for authentication, which Phase 1 replaces
 * with a real session.
 */
export function Topbar(): ReactElement {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { isOpen, toggle } = useSidebar();

  return (
    <header className="topbar" id="topbar">
      {/*
        The narrow-screen navigation toggle. `.menu-btn` is `display: none` until
        the prototype's 880px breakpoint, so this is invisible on a desktop
        without any width check here.

        `aria-expanded` and `aria-controls` are what make it a disclosure rather
        than an unlabelled icon: a screen reader announces both that it controls
        the navigation and whether that navigation is currently showing.
      */}
      <button
        type="button"
        className="menu-btn"
        onClick={toggle}
        aria-expanded={isOpen}
        aria-controls="sidebar"
        aria-label={t('toggle_navigation')}
      >
        <Icon name="menu" />
      </button>

      <div className="search-wrap">
        <GlobalSearch />
      </div>

      <div className="top-actions">
        {/*
          Theme only. The language toggle moved into the account menu, where
          Settings and Sign out already are — keeping it here as well would be
          two controls for one setting a few pixels apart. The sign-in screens
          still render both, because they have no menu to hold it.
        */}
        <PreferenceToggles withLanguage={false} />

        <NotificationBell />

        {user ? <UserMenu /> : null}
      </div>
    </header>
  );
}

/**
 * The signed-in identity, and what you can do with it.
 *
 * Collapsed into one menu because that is where people look for it — Settings,
 * language and sign out were three separate controls competing for space in a
 * row that also holds search, the theme toggle and notifications, and the one
 * with the most permanent consequence was an unlabelled icon beside them.
 *
 * The chip itself is the trigger, so the target is the name and avatar rather
 * than a separate caret: it is the largest thing in that corner and the thing a
 * person is already aiming at.
 *
 * A `menu`, not a `dialog` — it is a list of commands, and the role is what
 * tells a screen reader to announce it as one and to expose the item count.
 * Escape and click-outside both close it, following the notification panel.
 */
function UserMenu(): ReactElement | null {
  const { t } = useTranslation();
  const { user, signOut } = useAuth();
  const { language, toggleLanguage } = usePreferences();
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    function onPointerDown(event: MouseEvent): void {
      if (!wrapRef.current?.contains(event.target as Node)) setIsOpen(false);
    }
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') setIsOpen(false);
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  if (!user) return null;

  return (
    <div className="user-menu-wrap" ref={wrapRef}>
      <button
        type="button"
        className="user-chip as-button"
        onClick={() => setIsOpen((value) => !value)}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        /*
         * Named explicitly, because the visible text is not always there: the
         * name and company are hidden below the prototype's breakpoint, which
         * left this button with an aria-hidden avatar and a caret and no
         * accessible name at all — the only route to signing out, unlabelled.
         */
        aria-label={`${t('account_menu')} — ${user.name}`}
      >
        <div className="avatar" aria-hidden="true">
          {initialsOf(user.name)}
        </div>
        <div className="user-chip-text">
          {/*
            `<bdi>` for the same reason the security log needs it: a Latin name
            in an Arabic page has its trailing punctuation reordered, so
            "Layla A." reads as ".Layla A" to every Arabic user.
          */}
          <div className="nm">
            <bdi>{user.name}</bdi>
          </div>
          {/*
            The company, not the role. The prototype showed the role because its
            switcher changed it; here the useful fact is which company's data you
            are looking at, since that is what scopes every screen.
          */}
          <div className="rl">
            <bdi>{user.employer_name}</bdi>
          </div>
        </div>
        <Icon name="chevron" />
      </button>

      {isOpen ? (
        <div className="user-menu" role="menu">
          <button
            type="button"
            className="um-item"
            role="menuitem"
            onClick={() => {
              setIsOpen(false);
              navigate('/settings');
            }}
          >
            <Icon name="gear" />
            <span>{t('settings')}</span>
          </button>

          {/*
            The language shown is the one you would switch *to*, not the one you
            are in — a menu item is a verb. The counterpart in the top bar is a
            two-state toggle where showing both is right; here it would read as
            a status line.
          */}
          <button
            type="button"
            className="um-item"
            role="menuitem"
            onClick={() => {
              setIsOpen(false);
              toggleLanguage();
            }}
          >
            <Icon name="chat" />
            <span>{language === 'en' ? 'العربية' : 'English'}</span>
          </button>

          <div className="um-sep" role="separator" />

          <button
            type="button"
            className="um-item danger"
            role="menuitem"
            onClick={() => {
              setIsOpen(false);
              void signOut();
            }}
          >
            <Icon name="logout" />
            <span>{t('sign_out')}</span>
          </button>
        </div>
      ) : null}
    </div>
  );
}
